/**
 * 検索クエリのパース・正規化に関する共通ユーティリティ
 *
 * オポチュニティ検索（CustomSearchCaptain）と引合検索（InquirySearchParser）で
 * 共通利用される関数群。
 */

/**
 * クエリ文字列をスペースで分割（引用符内のスペースは保持）
 *
 * @param query - 分割対象のクエリ文字列
 * @param options.unquote - true の場合、各トークンから外側の引用符を除去する（パーサー内部用）
 */
export function splitQuery(query: string, options?: { unquote?: boolean }): string[] {
  const shouldUnquote = options?.unquote ?? false
  const results: string[] = []
  let current = ''
  let inQuotes = false
  let quoteChar = ''

  for (let i = 0; i < query.length; i++) {
    const char = query[i]

    if ((char === '"' || char === "'") && (i === 0 || query[i - 1] !== '\\')) {
      if (!inQuotes) {
        inQuotes = true
        quoteChar = char
        if (!shouldUnquote) current += char
      } else if (char === quoteChar) {
        inQuotes = false
        quoteChar = ''
        if (!shouldUnquote) current += char
      } else {
        current += char
      }
    } else if (char === ' ' && !inQuotes) {
      if (current) {
        results.push(current)
        current = ''
      }
    } else {
      current += char
    }
  }

  if (current) {
    results.push(current)
  }

  return results
}

/**
 * 文字列の先頭・末尾が同じ引用符で囲まれていれば除去する
 */
export function unquote(str: string): string {
  if ((str.startsWith('"') && str.endsWith('"')) || (str.startsWith("'") && str.endsWith("'"))) {
    return str.slice(1, -1)
  }
  return str
}

/**
 * スペースを含む文字列をダブルクォートで囲む
 */
export function quoteIfNeeded(str: string): string {
  return str.match(/\s/) ? `"${str}"` : str
}

/**
 * クエリ/除外リストの正規化
 *
 * - マイナスプレフィクスのクエリを除外リストに移動
 * - 空文字を除去
 * - 重複を排除
 */
export function normalizeQueryAndExclude(
  queries: string[],
  excludes: string[],
): { queries: string[]; excludes: string[] } {
  const newExcludes = [...excludes]

  // マイナスで始まるクエリを除外リストに移動
  for (const v of queries) {
    if (v.startsWith('-')) {
      newExcludes.push(unquote(v.substring(1)))
    }
  }

  const normalizedQueries = Array.from(new Set(queries.filter((v) => !v.startsWith('-')).filter((v) => !!v)))

  const normalizedExcludes = Array.from(
    new Set(newExcludes.map((v) => (v.startsWith('-') ? v.substring(1) : v)).filter((v) => !!v)),
  )

  return { queries: normalizedQueries, excludes: normalizedExcludes }
}

/**
 * アイテムの指定フィールドを結合してキーワード検索する
 *
 * @param item - 検索対象オブジェクト
 * @param query - 検索クエリ文字列
 * @param searchableKeys - 検索対象フィールドのキー配列
 * @param nameKeys - 名前フィールドのキー集合（スペース除去版も検索対象に追加）
 */
export function execSearchQuery<T extends Record<string, unknown>>(
  item: T,
  query: string,
  searchableKeys: (keyof T)[],
  nameKeys?: Set<keyof T>,
): boolean {
  const searchableFields = searchableKeys
    .map((key) => {
      const value = item[key]
      if (value == null) return ''
      if (nameKeys?.has(key)) {
        const strValue = String(value)
        return [strValue, strValue.replace(/\s/g, '')].join('\t')
      }
      return String(value)
    })
    .join('\t')

  return searchableFields.toLowerCase().includes(query.toLowerCase())
}

/**
 * 配列から重複を除去する（キー関数で同一性を判定）
 */
export function uniqBy<T>(arr: T[], iteratee: (item: T) => string): T[] {
  const seen = new Set<string>()
  return arr.filter((item) => {
    const key = iteratee(item)
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
}
